import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, model, output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { CalculateDocumentLineItemPriceFromDiscountedLineBackwards } from '@phoenix/common';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumber, InputNumberModule } from 'primeng/inputnumber';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, Subject } from 'rxjs';
import { IPHXEditComponent } from '../../../@core/interfaces/IPHXEditComponent.interface';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../@core/services/notification/notification.service';
import { DocumentLineItem } from '../../../generated/generated-types';
import { PhxInputNumberComponent } from '../../../shared/components/phx-input-number/phx-input-number.component';
import { LocaleDatePipe } from '../../../shared/pipes/locale-date.pipe';


/**
 * @description Displays discounted line price of a DLI, and recalculates on button click as follows: discountedLinePrice / quantity * (1 - (discount/100))
 */
@Component({
  selector: 'app-dli-line-sum-input',
  templateUrl: './dli-line-sum-input.component.html',
  //doesnt work with onPush yet!!
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DLILineSumInputComponent),
    multi: true // It can be multiple instances
  }],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputNumberModule,
    ButtonModule,
    TooltipModule,
    TranslatePipe,
    InputGroupModule,
    InputGroupAddonModule,
    PhxInputNumberComponent
  ]
})

export class DLILineSumInputComponent extends FieldType implements IPHXEditComponent, ControlValueAccessor {

  minFractionDigits: number;
  protected destroy$ = new Subject<void>();

  @Input()
  decimalPlaces: number;

  defaultValue: number = 0;
  dliLinePriceVisible: boolean = true;

  ngModel = model<number>();
  ngModelChange$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  onChange: (value: number) => void = (value) => { this.ngModel.set(value) }; // Value Accessor Related
  onTouch: () => void = () => { }; // Value Accessor Related
  editPrice = model<number>();

  @Input()
  dli: DocumentLineItem;

  itemPriceChange = output<Number>();

  isInputDisabled: boolean = true;

  @ViewChild('inputNumber') inputNumber: InputNumber;
  @ViewChild('inputEdit') inputEdit: PhxInputNumberComponent;

  constructor(
    private notificationService: NotificationService,
    private i18n: I18nService,
    private localeDatePipe: LocaleDatePipe
  ) {
    super();

  }

  ngAfterViewInit() {

    if (this.decimalPlaces || this.dli?.decimalPlaces)
      this.decimalPlaces = Number((this.dli?.decimalPlaces ?? this.decimalPlaces)?.toString().split('.')[1].length);

  }

  ngOnDestroy() {
    this.ngModelChange$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onModelChange($event) {
    this.ngModelChange$.next($event);
  }

  enableEditDiscountedLinePrice() {
    this.editPrice.set(this.ngModel());
    this.dliLinePriceVisible = false;
  }


  // Value Accessor Related
  // This method is called by the form control on startup to set the initial value
  writeValue(value: number): void {
    this.ngModel.set(value);
  }

  /**
   * @description This method is called by the form control when the input value changes.
   * Value Accessor Related
   * @param fn Any
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * @description This method is called by the form control when the input is touched.
   * Value Accessor Related
   * @param fn 
   */
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }


  /**
 * @description This method is called by the form control when the input value changes.
 * It triggers the onChange function to update the form control value, which was registered by the registerOnChange method!!!!
 * Value Accessor Related
 * @param value The value to be set
 */
  setValue(value: number): void {
    this.ngModel.set(value);
    this.onModelChange(value);
    this.onChange(value);
    this.onTouch();

    // Used indenpendently of the form control [Without the form control]
    this.ngModelChange$.next(value);
  }

  saveEditedDiscountedLinePrice() {
    this.dliLinePriceVisible = true;
    // console.log(this.inputEdit.value);
    if (this.inputEdit?.ngModel && this.dli?.quantity) {
      let linePrice = this.inputEdit?.ngModel();
      let itemPrice = CalculateDocumentLineItemPriceFromDiscountedLineBackwards(this.dli, linePrice);
      this.dli.itemPrice = itemPrice;
      this.itemPriceChange.emit(itemPrice);
      this.ngModelChange$.next(this.editPrice());
      this.ngModel.set(this.editPrice());
      // console.log(itemPrice);
      // this.lineItemDataService.setDocumentLineItem(this.dli)
      // this.lineItemDataService.setProductPriceInfo({
      //   price: this.dli.itemPrice,
      //   sourceType: PriceSourceType.MANUALPRICE
      // })
      if (this.field && this.options)
        this.options.detectChanges(this.field);
    }
    else {
      this.notificationService.error(_('document-line-item.value-or-quantity-missing'));
    }
  }
}

